'use strict';

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

export default function Autocomplete({
  arrData,
  onChange,
  initialValue,
  currentSegment,
  onTextChange,
  searchServer = false,
  hintText = 'Search segment'
}) {
  const autocompleteRef = useRef();
  const inputRef = useRef();

  const [value, setValue] = useState(initialValue);
  const [, setChosenOption] = useState(currentSegment);
  const [showOptions, setShowOptions] = useState(false);
  const [data, setData] = useState(arrData);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setData(arrData || []);
  }, [arrData]);

  function handleClickOutside(event) {
    if (
      autocompleteRef.current &&
      !autocompleteRef.current.contains(event.target)
    ) {
      setShowOptions(false);
    }
  }

  function searchResults() {
    let result = data;
    if (searchServer) return arrData;

    if (searchTerm.length > 0) {
      result = data.filter((item) => {
        return item.name.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    return result;
  }

  function reset(ev) {
    ev.preventDefault();
    setValue('');
    setChosenOption(null);
  }

  function handleInput(ev) {
    setValue(ev.target.value);
    setSearchTerm(ev.target.value);
    setShowOptions(true);

    if (onTextChange) onTextChange(ev.target.value);
  }

  const handleClick = (item) => {
    inputRef.current.blur();
    setValue(item.name);
    setChosenOption(item);
    setShowOptions(false);
    onChange(item);
  };

  function handleFocus() {
    setShowOptions(true);
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={value}
        placeholder={hintText}
        onChange={handleInput}
        onFocus={handleFocus}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:shadow-outline"
      />
      {showOptions && (
        <span
          onClick={reset}
          className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </span>
      )}
      {showOptions ? (
        <div
          ref={autocompleteRef}
          tabIndex="0"
          className="absolute z-50 w-full mt-1 overflow-hidden overflow-y-scroll bg-white border border-gray-100 shadow-md focus:border-purple-400 focus:outline-none focus:shadow-outline-purple mh-48 rounded-md"
        >
          <ul className="py-1">
            {searchResults().length > 0 ? (
              searchResults().map((item, index) => {
                return (
                  <li
                    onClick={() => {
                      handleClick(item);
                    }}
                    key={index}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-200"
                  >
                    {item.name}
                  </li>
                );
              })
            ) : (
              <li className="px-3 py-2 text-center">No matching results</li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

Autocomplete.propTypes = {
  arrData: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  initialValue: PropTypes.string,
  currentSegment: PropTypes.object,
  onTextChange: PropTypes.func,
  clearOnBlur: PropTypes.bool,
  searchServer: PropTypes.bool,
  hintText: PropTypes.string
};
